import { TrackedObject } from 'tracked-built-ins';
import { getOwner } from '@ember/application';
import { tracked } from '@glimmer/tracking';
import AwayMessageEditHandler from '../components/messaging-routes/away-message-edit-handler.js';
import AwayMessageEditPage from '../components/messaging-routes/away-message-edit.gjs';
import ContactsHandler from '../components/messaging-routes/contacts-handler.js';
import ContactsPage from '../components/messaging-routes/contacts.gjs';
import ConversationHandler from '../components/messaging-routes/conversation-handler.js';
import ConversationListHandler from '../components/messaging-routes/conversation-list-handler.js';
import ConversationListPage from '../components/messaging-routes/conversation-list.gjs';
import ConversationPage from '../components/messaging-routes/conversation.gjs';
import Service from '@ember/service';

/**
 * Route map defining all available messaging routes with their associated
 * components and handlers.
 * @type {Object}
 */
export const ROUTE_MAP = {
  awayMessageEdit: {
    name: 'awayMessageEdit',
    component: AwayMessageEditPage,
    handler: AwayMessageEditHandler,
  },
  contacts: {
    name: 'contacts',
    component: ContactsPage,
    handler: ContactsHandler,
  },
  conversation: {
    name: 'conversation',
    component: ConversationPage,
    handler: ConversationHandler,
  },
  conversationList: {
    name: 'conversationList',
    component: ConversationListPage,
    handler: ConversationListHandler,
  },
};

/**
 * Manages navigation between different routes within the messaging client.
 * This service handles route state management and coordinates the lifecycle
 * of route handlers (onVisit/onLeave hooks) when navigating between routes.
 * @class MessagingNavigationService
 * @extends Service
 */
export default class MessagingNavigationService extends Service {
  /**
   * The currently active route.
   * @type {Object|null}
   * @property {Component} component - The component to render for this route
   * @property {Object} handler - The route handler instance with onVisit/onLeave hooks
   * @property {string} name - The name of the route
   */
  @tracked currentRoute;

  constructor() {
    super(...arguments);
    this.#navigateTo(ROUTE_MAP.conversationList);
  }

  /**
   * Checks if the provided route(s) match the current route.
   * @param {Object|Object[]} routes - A single route object or array of route objects to check
   * @returns {boolean} True if any of the provided routes match the current route
   */
  isCurrentRoute(routes) {
    if (Array.isArray(routes)) {
      return routes.some(route => route.name === this.currentRoute.name);
    }

    return routes.name === this.currentRoute.name;
  }

  async toConversationList() {
    await this.#navigateTo(ROUTE_MAP.conversationList);
  }

  async toConversation(conversation) {
    await this.#navigateTo(ROUTE_MAP.conversation, { conversation });
  }

  async toContacts() {
    await this.#navigateTo(ROUTE_MAP.contacts);
  }

  async toAwayMessageSettings() {
    await this.#navigateTo(ROUTE_MAP.awayMessageEdit);
  }

  /**
   * Internal method that handles navigation between routes.
   * @private
   * @param {Object} route - Route definition from ROUTE_MAP
   * @param {Object} [options={}] - Options to pass to the route handler constructor
   * @returns {Promise<void>} Resolves when navigation is complete
   */
  async #navigateTo(route, options = {}) {
    if (this.currentRoute) {
      await this.currentRoute.handler.onLeave.perform();
    }

    this.currentRoute = this.#buildCurrentRoute(route, options);
    await this.currentRoute.handler.onVisit.perform();
  }

  /**
   * Builds a tracked route object with the component, handler instance, and route name.
   * @private
   * @param {Object} route - Route definition from ROUTE_MAP
   * @param {Object} options - Options to pass to the route handler constructor
   * @returns {TrackedObject} The constructed route object
   */
  #buildCurrentRoute(route, options) {
    return new TrackedObject({
      component: route.component,
      // eslint-disable-next-line new-cap
      handler: new route.handler(getOwner(this), options),
      name: route.name,
    });
  }
}
